import React, { useState } from 'react';
import { FaUserShield, FaUserCircle } from 'react-icons/fa';
import ResetPasswordButton from '../ResetPasswordButton';
import MappingModal from './MappingModal';

/**
 * Clients tab — Super Admin can:
 *   • See every client with their current advisor mapping
 *   • Click "Map" to change/assign an advisor (any advisor in the system)
 *   • Reset client password
 */
const AdminClientsTab = ({ clientsOverview, advisors = [], onMapAdvisor }) => {
  const [mapping, setMapping] = useState(null); // { client, currentAdvisorId } | null

  const closeModal = () => setMapping(null);

  const options = (advisors || [])
    .filter(a => a.is_active !== false)
    .map(a => ({ id: a.id, name: a.name, subtitle: a.email }));

  const clients = Array.isArray(clientsOverview) ? clientsOverview : clientsOverview?.clients || [];

  return (
    <div className="space-y-6" data-testid="admin-clients-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Client Overview</h2>
        {clientsOverview && (
          <span className="text-[#FFD700] font-bold">Total: {clients.length} clients</span>
        )}
      </div>

      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0F172A]">
              <tr className="text-gray-400 text-sm">
                <th className="px-4 py-3 text-left">Client Name</th>
                <th className="px-4 py-3 text-left">Email/Phone</th>
                <th className="px-4 py-3 text-left">Mapped Advisor</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.id} className="border-b border-[#2D5A8F]/50 hover:bg-[#1E3A5F]/50">
                  <td className="px-4 py-3 text-white font-medium">{client.name}</td>
                  <td className="px-4 py-3 text-gray-300 text-sm">{client.email || client.contact || '-'}</td>
                  <td className="px-4 py-3 text-sm" data-testid={`client-mapped-adv-${client.id}`}>
                    {client.advisor_name ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-200 border border-purple-500/30 text-xs">
                        <FaUserCircle size={11} /> {client.advisor_name}
                      </span>
                    ) : (
                      <span className="text-gray-500 italic text-xs">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm">{client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      {onMapAdvisor && (
                        <button
                          onClick={() => setMapping({ client, currentAdvisorId: client.advisor_id || null })}
                          data-testid={`open-map-advisor-${client.id}`}
                          className="px-3 py-1.5 bg-purple-500/15 border border-purple-500/40 text-purple-200 rounded-md text-xs font-medium hover:bg-purple-500/25 transition-all flex items-center gap-1.5"
                        >
                          <FaUserShield size={11} /> {client.advisor_id ? 'Re-Map' : 'Map'}
                        </button>
                      )}
                      {(client.user_id || client.id) && (
                        <ResetPasswordButton
                          userId={client.user_id || client.id}
                          userName={client.name}
                          userEmail={client.email || client.contact}
                          userRole="client"
                          resetEndpoint="admin"
                          compact={true}
                          className="p-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded transition-colors"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {clients.length === 0 && (
          <p className="text-gray-400 text-center py-8">No clients found</p>
        )}
      </div>

      {mapping && (
        <MappingModal
          isOpen={!!mapping}
          onClose={closeModal}
          title="Map Client to Advisor"
          subjectLabel="Client"
          subjectName={mapping.client.name}
          targetLabel="Advisor"
          options={options}
          currentTargetId={mapping.currentAdvisorId}
          onSubmit={(advisorId) => onMapAdvisor(mapping.client.id, advisorId)}
        />
      )}
    </div>
  );
};

export default AdminClientsTab;
